import { nextFrame } from './helper'
import { getPrograms } from './programs'
import { detect } from 'detect-browser'
import TWEEN from '@tweenjs/tween.js'
import Menu from './Menu'
import { MODE, MQ_TABLET } from './const'
import { Lethargy } from 'lethargy'
import WebFont from 'webfontloader'
import Panel from './elements/Panel'
import Cookies from 'js-cookie'

const lethargy = new Lethargy()
const browser = detect() //

let menu = null
let menuItemSlugs = null
let activeSection = null
let lastActiveSection = null
let container = null
let loader = null
let canvas = null
let gl = null
let hasNoWebGl = false
let isAnimating = false
let isTouchStarted = false

let hash = ''
let sections = []

let index = -1
let width = 0
let height = 0
let aspectRatio = 1
let startY = 0
let startX = 0
let deltaY = 0
let deltaX = 0
let touchStartTime = 0

init()

async function init() {
	const consent = Cookies.get('cookie-consent')
	const cookieButton = document.querySelector('.js-cookie-button')

	if (consent === 'accepted') {
		// @ts-ignore
		window.consentGranted()
	} else {
		cookieButton.parentElement.classList.add('is-visible')
		cookieButton.addEventListener('click', () => {
			Cookies.set('cookie-consent', 'accepted', { expires: 365 })
			cookieButton.parentElement.classList.remove('is-visible')
			// @ts-ignore
			window.consentGranted()
		})
	}

	const mq = window.matchMedia(`(min-width: ${MQ_TABLET}) and (orientation:landscape)`)
	const mode = mq.matches ? MODE.LANDSCAPE : MODE.PORTRAIT

	menu = new Menu(document.querySelector('.js-menu'), mode)
	menuItemSlugs = Array.from(document.querySelectorAll('.js-menu-item')).map(
		(menuItem) => (menuItem as HTMLElement).dataset.slug
	)
	loader = document.querySelector('.js-loader')
	container = document.querySelector('.js-main')
	canvas = container.querySelector('.js-canvas')

	const params = {
		antialias: window.devicePixelRatio < 2,
		stencil: true,
		alpha: false,
		powerPreference: 'high-performance'
	}

	gl = canvas.getContext('webgl', params) || canvas.getContext('experimental-webgl', params)

	if (!gl) {
		hasNoWebGl = true
		document.body.classList.add('no-webgl')
		return true
	}

	await loadFonts()
	const programs = getPrograms(gl)

	const sectionNodes = Array.from(document.querySelectorAll('.js-panel'))
	sections = sectionNodes.map((section) => new Panel(section, mode, browser))
	sections = sections.sort((a, b) => menuItemSlugs.indexOf(a.uid) - menuItemSlugs.indexOf(b.uid))
	sections.forEach((section) => section.init(gl, programs, showMenu, hideMenu))

	gl.clearColor(1, 1, 1, 1)

	gl.disable(gl.DEPTH_TEST)
	gl.enable(gl.BLEND)
	gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA)

	await Promise.all(sections.map((section) => section.preload(gl, onLoadProgress)))

	for (let i = 0; i < sections.length; i++) {
		await nextFrame()
		sections[i].visible = true
		sections[i].draw(gl)
		sections[i].visible = false
		setLoader((i / sections.length) * 10 + 90)
	}

	setLoader(100)

	resize()
	onHashChange()

	mq.addListener(onMQChange)
	window.addEventListener('resize', resize)
	window.addEventListener('hashchange', onHashChange)
	window.addEventListener('wheel', onWheel)
	document.body.addEventListener('touchstart', onStart)
	document.body.addEventListener('touchmove', onMove)
	document.body.addEventListener('touchcancel', onEnd)
	document.body.addEventListener('touchend', onEnd)
	requestAnimationFrame(update)

	await nextFrame()
	container.style.opacity = 1
}

async function loadFonts() {
	return new Promise((resolve) => {
		WebFont.load({
			custom: {
				families: ['BrownPro-Bold', 'BrownPro-Light']
			},
			active: resolve
		})
	})
}

function onStart(event) {
	isTouchStarted = true
	const touches = event.touches && event.touches.length ? event.touches[0] : event

	startX = touches.clientX
	startY = touches.clientY
	deltaX = 0
	deltaY = 0
	touchStartTime = performance.now()
}

function onMove(event) {
	if (!isTouchStarted) return

	const touches = event.touches && event.touches.length ? event.touches[0] : event
	deltaX = touches.clientX - startX
	deltaY = touches.clientY - startY
}

function onEnd(event) {
	isTouchStarted = false
	if (isAnimating) return
	if (isMenu(event.target)) return

	const duration = performance.now() - touchStartTime
	let isSwipe = false
	let forward = false

	if (duration < 300) {
		if (deltaY > 25) {
			isSwipe = true
		} else if (deltaY < -25) {
			isSwipe = true
			forward = true
		}

		if (isSwipe) {
			setHash(forward)
		}
	}
}

function onLoadProgress() {
	const loaded = sections.filter((section) => section.isLoaded)
	const progress = loaded.length / sections.length
	setLoader(progress * 90)
}

function setLoader(progress) {
	loader.textContent = `Loading ${Math.round(progress)}%`
}

function onWheel(event) {
	if (lethargy.check(event) === false) return
	if (isAnimating) return
	if (isMenu(event.target)) return
	const forward = event.deltaY > 0
	setHash(forward)
}

function isMenu(node) {
	let isMenu = false
	while (node.parentElement) {
		if (node.classList.contains('js-menu')) {
			isMenu = true
			break
		}
		node = node.parentElement
	}

	return isMenu
}

function setHash(forward) {
	if (index === 1 && !forward) {
		window.location.hash = ''
		history.replaceState(null, null, ' ')
	} else if (forward && index + 1 < sections.length) {
		window.location.hash = sections[index + 1].uid
	} else if (!forward && index - 1 >= 0) {
		window.location.hash = sections[index - 1].uid
	}
}

async function onHashChange() {
	const newHash = window.location.hash.replace('#', '')
	const oldHash = hash
	const newIndex = !newHash ? 0 : sections.findIndex((section) => section.uid === newHash)
	const forward = index < newIndex

	lastActiveSection = activeSection
	activeSection = sections[newIndex]

	index = newIndex
	hash = newHash

	menu.update(hash)

	isAnimating = true

	await Promise.all([
		lastActiveSection ? lastActiveSection.animateOut(forward, hash, oldHash) : Promise.resolve(),
		activeSection.animateIn(forward, hash, oldHash)
	])

	showMenu()
	lastActiveSection = null
	isAnimating = false
}

function hideMenu() {
	menu.hide()
}

function showMenu() {
	const { isMenuInverted, isLogoInverted } = activeSection
	menu.show(isMenuInverted, isLogoInverted)
}

function onMQChange() {
	window.location.reload()
}

function resize() {
	const dpr = Math.min(window.devicePixelRatio, 2)
	const rect = container.getBoundingClientRect()
	width = Math.round(rect.width * dpr)
	height = Math.round(rect.height * dpr)
	aspectRatio = height / width
	canvas.width = width
	canvas.height = height
	sections.forEach((section) => section.resize(width, height, dpr))
}

function update() {
	requestAnimationFrame(update)
	TWEEN.update()

	gl.clear(gl.COLOR_BUFFER_BIT)
	gl.viewport(0, 0, width, height)

	if (lastActiveSection) {
		lastActiveSection.draw(gl)
	}

	if (activeSection) {
		activeSection.draw(gl)
	}
}

export function getAspectRatioLandscape() {
	return aspectRatio
}

export function getAspectRatioPortrait() {
	return 1 / aspectRatio
}
